import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, take } from 'rxjs';
import { AppTools } from 'src/app/shared/interfaces/appTools.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { environment } from 'src/environments/environment';
import type FireBase from 'firebase/compat';
import { MenuTool } from '../components/pages-container/interfaces/MenuTool.interface';

declare const firebase: typeof FireBase;
declare const firebaseConfig: any;

@Injectable({
  providedIn: 'root',
})
export class AppToolsService {
  readonly #api = inject(ApiService);

  public getAllTools(): Observable<MenuTool[]> {
    return this.#api.GetConfigurations().pipe(
      take(1),
      switchMap(config => {
        firebaseConfig.apiKey = config.fireBase.apiKey;
        this.initializeFirebase();
        return this.#api.GetAllToolsApi();
      }),
      switchMap(tools =>
        this.#api
          .getGoogleCustomToken()
          .pipe(
            switchMap(token =>
              this.signInWithFirebase(token.customToken.result).pipe(
                switchMap(() => this.prepareTools(tools)),
              ),
            ),
          ),
      ),
    );
  }

  private initializeFirebase() {
    firebaseConfig.storageBucket = environment?.firebase?.storageBucket;
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
      firebase.analytics();
    }
  }

  private signInWithFirebase(token: string): Observable<void> {
    return new Observable(observer => {
      firebase
        .auth()
        .signInWithCustomToken(token)
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch(error => observer.error(error));
    });
  }

  private prepareTools(tools: AppTools): Observable<MenuTool[]> {
    return new Observable(observer => {
      const appTools: MenuTool[] = [];

      Promise.all(
        tools.data.map(async element => {
          appTools.push({
            id: element.id,
            publicUrl: this.checkIfHttpExists(element.publicUrl),
            name: element.name,
            iconImageUrl: element.iconImage,
            logoImageUrl: element.logoImage,
            urls: element.urls,
          } as MenuTool);
        }),
      ).then(() => {
        appTools.sort((a, b) => a.id - b.id);
        observer.next(appTools);
        observer.complete();
      });
    });
  }

  async fetchImageAsBase64(path: string, extension: string): Promise<string> {
    const storage = firebase.storage();
    const ref = storage.ref(`/${path}${extension}`);
    const url = await ref.getDownloadURL();
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return `data:image/${extension.slice(1)};base64,${btoa(
      String.fromCharCode(...new Uint8Array(arrayBuffer)),
    )}`;
  }

  private checkIfHttpExists(url: string): string {
    if (url.includes('http://') || url.includes('https://')) {
      return url;
    }
    return `https://${url}`;
  }
}
